<template>
  <section
    class="dataset-contact-form"
    :class="`dataset-contact-form--${mode}`"
    :aria-label="formTitle"
  >
    <b-overlay
      no-wrap
      :show="loading"
      :variant="'white'"
      :opacity="0.7"
      :blur="'2px'"
      rounded="sm"
    />

    <header class="dataset-contact-form__head">
      <span class="dataset-contact-form__title">
        {{ formTitle }}
      </span>
      <b-button
        v-if="allowDelete"
        variant="link"
        class="dataset-contact-form__dismiss"
        :aria-label="$t('buttons.cancel')"
        @click.stop="$emit('delete', formData.order)"
      >
        <b-icon-x-lg aria-hidden="true" />
      </b-button>
    </header>

    <b-form class="dataset-contact-form__body">
      <div
        v-if="showMain"
        class="dataset-contact-form__main"
      >
        <b-form-checkbox
          :id="`contact-${formData.order}-main-contact`"
          :key="`contact-${formData.order}-main-${formData.main ? '1' : '0'}`"
          v-model="formData.main"
          :name="`main-contact-${formData.order}`"
          @change="setMainContact"
        >
          Contact principal
        </b-form-checkbox>
      </div>

      <div class="dataset-contact-form__grid">
        <b-form-group
          class="dataset-contact-form__field"
          :label="$t('forms.contact.name')"
          :label-for="`dataset-contact-${formData.order}-name`"
          label-class="required"
        >
          <ValidationProvider
            ref="nameField"
            v-slot="{ classes, errors }"
            :name="`Nom contact ${formData.order}`"
            rules="required"
          >
            <b-form-input
              :id="`dataset-contact-${formData.order}-name`"
              v-model="formData.contact.name"
              v-sanitize
              :class="classes"
              :disabled="identityLocked"
              :placeholder="$t('forms.contact.name')"
              @blur="handleBlur"
            />
            <span class="form-errors">{{ errors[0] }}</span>
          </ValidationProvider>
        </b-form-group>

        <b-form-group
          class="dataset-contact-form__field"
          :label="$t('forms.contact.position')"
          :label-for="`dataset-contact-${formData.order}-position`"
        >
          <ValidationProvider
            v-slot="{ classes, errors }"
            :name="`Position contact ${formData.order}`"
          >
            <b-form-input
              :id="`dataset-contact-${formData.order}-position`"
              v-model="formData.contact.position"
              v-sanitize
              :class="classes"
              :disabled="identityLocked"
              :placeholder="$t('forms.contact.position')"
              @blur="handleBlur"
            />
            <span class="form-errors">{{ errors[0] }}</span>
          </ValidationProvider>
        </b-form-group>

        <b-form-group
          class="dataset-contact-form__field"
          :label="$t('forms.contact.email')"
          :label-for="`dataset-contact-${formData.order}-email`"
          label-class="required"
        >
          <ValidationProvider
            ref="emailField"
            v-slot="{ classes, errors }"
            :name="`Email contact ${formData.order}`"
            rules="required|email"
          >
            <b-form-input
              :id="`dataset-contact-${formData.order}-email`"
              v-model="formData.contact.email"
              v-sanitize
              type="email"
              autocomplete="email"
              :class="classes"
              :disabled="identityLocked"
              :placeholder="$t('forms.contact.email')"
              @blur="handleBlur"
            />
            <span class="form-errors">{{ errors[0] }}</span>
          </ValidationProvider>
        </b-form-group>

        <b-form-group
          class="dataset-contact-form__field"
          :label="$t('forms.contact.phone')"
          :label-for="`dataset-contact-${formData.order}-phone`"
        >
          <b-form-input
            :id="`dataset-contact-${formData.order}-phone`"
            v-model="formData.contact.phone_number"
            v-sanitize
            type="tel"
            autocomplete="tel"
            :disabled="identityLocked"
            :placeholder="$t('forms.contact.phone')"
            @blur="handleBlur"
          />
        </b-form-group>

        <template v-if="!usergroupDisabled">
          <b-form-group
            class="dataset-contact-form__field"
            :label="$t('forms.contact.usergroup.label')"
            :label-for="`dataset-contact-${formData.order}-usergroup`"
            label-class="required"
          >
            <ValidationProvider
              ref="usergroupField"
              v-slot="{ classes, errors }"
              :rules="usergroupDisabled ? '' : 'required'"
              :name="`Groupe contact ${formData.order}`"
            >
              <div
                class="dataset-contact-form__control"
                :class="classes"
              >
                <SearchUsergroups
                  :id="`dataset-contact-${formData.order}-usergroup`"
                  v-model="formData.contact.usergroup"
                  :types="[]"
                  :placeholder="$t('forms.contact.usergroup.placeholder')"
                  :usergroup="formData.contact.usergroup"
                  :reset-after="false"
                  :limit="100"
                  :allow-null="true"
                  :disabled="usergroupDisabled || identityLocked"
                  @select="setUsergroup"
                  @close="setUsergroup"
                />
                <span class="form-errors">{{ errors[0] }}</span>
              </div>
            </ValidationProvider>
          </b-form-group>

          <b-form-group
            class="dataset-contact-form__field"
            :label="$t('forms.contact.role')"
            :label-for="`dataset-contact-${formData.order}-role`"
          >
            <Multiselect
              :id="`dataset-contact-${formData.order}-role`"
              v-model="formData.role"
              :options="datasetsRoles"
              track-by="value"
              label="label"
              select-label=""
              selected-label=""
              deselect-label=""
              :searchable="false"
              :placeholder="$t('forms.contact.role')"
              @blur="handleBlur"
            />
          </b-form-group>
        </template>
      </div>
      <footer
        v-if="mode === 'create'"
        class="dataset-contact-form__footer"
      >
        <b-button
          variant="primary"
          type="button"
          :disabled="loading"
          @click.prevent="submitCreate"
        >
          {{ $t('buttons.create') }}
        </b-button>
      </footer>
    </b-form>
  </section>
</template>

<script>
import { mapState, mapMutations } from 'vuex';
import { usergroupAPI } from '@/api';
import i18n from '@/common/i18n/i18n.js';
import { usergroupId } from '@/app/dataset/utils';

import Multiselect from 'vue-multiselect';
import SearchUsergroups from '@/components/Searchbars/SearchUsergroups.vue';

import { ValidationProvider, extend, configure, setInteractionMode } from 'vee-validate';
import { required, email } from 'vee-validate/dist/rules';

extend('required', {
  ...required,
  message: i18n.messages[i18n.locale].words.requiredField
});
extend('email', {
  ...email,
  message: 'Veuillez entrer une adresse e-mail valide'
});
configure({
  classes: {
    valid: 'is-valid',
    invalid: 'is-invalid'
  }
});
setInteractionMode('passive');

export default {
  name: 'DatasetContactForm',

  components: {
    Multiselect,
    SearchUsergroups,
    ValidationProvider
  },

  props: {
    contact: {
      type: Object,
      default: () => {
        return {
          order: 1,
          id: null,
          role: null,
          main: false,
          contact: {
            id: null,
            name: null,
            position: null,
            email: null,
            usergroup: null,
            phone_number: null,
          }
        };
      }
    },
    currentContacts: {
      type: Array,
      default: () => {
        return [];
      }
    },
    usergroupDisabled: {
      type: Boolean,
      default: false
    },
    allowDelete: {
      type: Boolean,
      default: true
    },
    showMain: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: null,
    },
    mode: {
      type: String,
      default: 'create',
      validator(value) {
        return ['create', 'readonly', 'edit'].includes(value);
      },
    },
  },

  data() {
    return {
      loading: false,
      formData: {
        order: 1,
        id: null,
        role: null,
        main: false,
        contact: {
          id: null,
          name: null,
          position: null,
          email: null,
          usergroup: null,
          phone_number: null,
        }
      }
    };
  },

  computed: {
    ...mapState('datasets', [
      'datasetsRoles',
    ]),

    identityLocked() {
      return this.mode === 'readonly';
    },

    formTitle() {
      if (this.title) {
        return this.title;
      }
      if (this.mode === 'edit') {
        return this.formData.contact.name || this.$t('buttons.edit');
      }
      if (this.mode === 'create' && this.usergroupDisabled) {
        return this.$t('groupDetail.contacts.create');
      }
      return `Contact ${this.formData.order}`;
    },
  },

  watch: {
    formData: {
      deep: true,
      handler(newValue) {
        if (newValue) {
          this.$emit('change', {
            order: this.formData.order,
            id: this.formData.id,
            role: this.formData.role,
            main: this.formData.main,
            contact: {
              id: this.formData.contact.id,
              name: this.formData.contact.name,
              position: this.formData.contact.position,
              email: this.formData.contact.email,
              phone_number: this.formData.contact.phone_number,
              usergroup: usergroupId(this.formData.contact.usergroup)
            }
          });
        }
      }
    }
  },

  async created() {
    this.loading = true;
    if (!this.userSession.isSuperuser()) {
      this.SET_USERGROUPS_FILTERS({
        filter: 'user__role__in',
        value: '2'
      });
    }
    this.formData.order = this.contact.order;
    this.formData.id = this.contact.id;
    this.formData.contact.id = this.contact.contact.id;
    if (this.contact.role) {
      this.formData.role = this.datasetsRoles.find(el => el.value === this.contact.role);
    }
    this.formData.main = !!this.contact.main;
    this.formData.contact.name = this.contact.contact.name;
    this.formData.contact.position = this.contact.contact.position;
    this.formData.contact.email = this.contact.contact.email;
    this.formData.contact.phone_number = this.contact.contact.phone_number;
    if (this.contact.contact.usergroup) {
      const usergroup =
        await usergroupAPI.getUsergroup(this.contact.contact.usergroup.id ?
          this.contact.contact.usergroup.id :
          this.contact.contact.usergroup
        );
      if (usergroup) {
        this.formData.contact.usergroup = usergroup;
      }
    } else if (this.$config.isSaas) {
      this.formData.contact.usergroup = this.currentDomainOrganisation.value;
    }
    this.loading = false;
  },

  methods: {
    ...mapMutations('usergroups', [
      'SET_USERGROUPS_FILTERS'
    ]),

    setUsergroup(e) {
      this.formData.contact.usergroup = e;
    },

    getCreatePayload() {
      return {
        name: this.formData.contact.name,
        email: this.formData.contact.email,
        position: this.formData.contact.position,
        phone_number: this.formData.contact.phone_number,
        usergroup_id: usergroupId(this.formData.contact.usergroup),
      };
    },

    async submitCreate() {
      const providers = [this.$refs.nameField, this.$refs.emailField].filter(Boolean);
      const results = await Promise.all(providers.map(provider => provider.validate()));
      let usergroupValid = true;
      if (!this.usergroupDisabled && this.$refs.usergroupField) {
        usergroupValid = !!usergroupId(this.formData.contact.usergroup);
        const message = i18n.messages[i18n.locale].words.requiredField;
        this.$refs.usergroupField.applyResult({
          valid: usergroupValid,
          errors: usergroupValid ? [] : [message],
          failedRules: usergroupValid ? {} : { required: message }
        });
      }
      if (!results.every(result => result.valid) || !usergroupValid) {
        return;
      }
      this.$emit('create', this.formData.order);
    },

    handleBlur() {
      if (
        this.formData.contact.name !== null &&
        this.formData.contact.email !== null &&
        this.formData.contact.usergroup !== null
      ) {
        this.$emit('allowAdd', true);
      } else {
        this.$emit('allowAdd', false);
      }
    },

    setMainContact() {
      this.$emit('set-main', this.formData.order);
    },
    unsetMainContact(order) {
      if (this.formData.order !== order) {
        this.formData.main = false;
      }
    }
  }
};
</script>

<style lang="less" scoped>
.dataset-contact-form {
  box-sizing: border-box;
  position: relative;
  margin: 1rem 0 1.25rem;
  padding: 1rem 1.25rem 1.125rem;
  text-align: left;
  background-color: #fff;
  border: 1px solid #e2eaef;
  border-radius: 10px;
}

.dataset-contact-form--create {
  background-color: mix(@blue, #fcfdfe, 5%);
  border-color: mix(@blue, #e2eaef, 22%);
}

.dataset-contact-form__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid mix(@blue, #e2eaef, 14%);
}

.dataset-contact-form__title {
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: mix(#2f3234, @blue, 40%);
}

.dataset-contact-form__dismiss {
  flex-shrink: 0;
  padding: 0.25rem 0.35rem;
  margin: -0.25rem -0.35rem -0.25rem 0;
  line-height: 1;
  color: mix(#2f3234, @blue, 35%);
  border: none;
  transition: color 0.15s ease-out, transform 0.15s ease-out;

  &:hover,
  &:focus {
    color: @blue;
    text-decoration: none;
  }

  &:hover {
    transform: scale(1.06);
  }
}

.dataset-contact-form__body {
  margin: 0;
}

.dataset-contact-form__main {
  margin-bottom: 0.75rem;

  ::v-deep .custom-control-label {
    font-size: 0.8125rem;
    font-weight: 600;
    color: #2f3234;
    line-height: 1.8;
  }
}

.dataset-contact-form__grid {
  display: grid;
  gap: 0.75rem 1rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

@media (max-width: 540px) {
  .dataset-contact-form__grid {
    grid-template-columns: minmax(0, 1fr);
  }
}

.dataset-contact-form__field {
  width: 100%;
  min-width: 0;
  margin-bottom: 0;

  ::v-deep label {
    font-size: 0.8125rem;
    font-weight: 600;
    color: #2f3234;
    margin-bottom: 0.35rem;
  }

  ::v-deep .form-control,
  ::v-deep select.custom-select {
    width: 100%;
    max-width: 100%;
  }

  ::v-deep .multiselect {
    min-height: 38px;
  }

  ::v-deep .multiselect__tags {
    min-height: 38px;
    padding: 0.375rem 2.5rem 0 0.75rem;
    border-color: #ced4da;
    border-radius: 0.25rem;
  }

  ::v-deep .multiselect__placeholder,
  ::v-deep .multiselect__single {
    font-size: 1rem;
    margin-bottom: 0;
    padding-top: 0;
  }
}

.dataset-contact-form__control {
  width: 100%;
}

.dataset-contact-form__footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
  padding-top: 0.75rem;
  border-top: 1px solid mix(@blue, #e2eaef, 14%);

  ::v-deep .btn-primary {
    font-size: 1rem;
    padding: 0.4rem 1.25rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .dataset-contact-form__dismiss {
    transition: none;

    &:hover {
      transform: none;
    }
  }
}
</style>
